import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import aboutHero1 from "@/assets/about-hero-1-new.png";
import aboutHero2 from "@/assets/about-hero-2.avif";
import aboutHero3 from "@/assets/about-hero-3.avif";
import sameerKhanPhoto from "@/assets/team-sameer-khan.jpg";
import juanGarciaPhoto from "@/assets/team-juan-garcia.png";
import adhamPhoto from "@/assets/team-adham.png";
import michellePhoto from "@/assets/team-michelle.png";
import salahPhoto from "@/assets/team-salah.png";
import caseyPhoto from "@/assets/team-casey.png";

// Animated section wrapper
const AnimatedSection = ({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Hero Section with 3-image layout
const AboutHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });

  const y1 = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -30]);

  return (
    <section ref={ref} className="pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-4xl mx-auto text-center mb-16 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="inline-block px-4 py-1.5 mb-6 text-xs font-medium tracking-wide uppercase text-primary bg-primary/10 rounded-full">
              About Ayra
            </span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="text-4xl md:text-5xl lg:text-6xl font-semibold text-foreground tracking-tight leading-[1.1] mb-6"
          >
            We're building the future of{" "}
            <span className="text-primary">AI delivery</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto"
          >
            A platform born from the frustration of watching brilliant AI agencies
            drown in operational chaos. We believe there's a better way.
          </motion.p>
        </div>

        {/* 3-Image Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-5xl mx-auto">
          {/* Large image on left */}
          <motion.div
            style={{ y: y1 }}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            whileHover={{ y: -4, scale: 1.01 }}
            className="relative aspect-[4/5] md:row-span-2 rounded-2xl overflow-hidden group cursor-default shadow-lg hover:shadow-xl transition-shadow duration-500"
          >
            <img
              src={aboutHero1}
              alt="About Ayra"
              className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/10 via-transparent to-transparent opacity-60" />
          </motion.div>

          {/* Two stacked images on right */}
          <motion.div
            style={{ y: y2 }}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            whileHover={{ y: -4, scale: 1.01 }}
            className="relative aspect-[4/3] md:aspect-auto md:min-h-[200px] rounded-2xl overflow-hidden group cursor-default shadow-lg hover:shadow-xl transition-shadow duration-500"
          >
            <img
              src={aboutHero2}
              alt="About Ayra"
              className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/10 via-transparent to-transparent opacity-60" />
          </motion.div>

          <motion.div
            style={{ y: y2 }}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            whileHover={{ y: -4, scale: 1.01 }}
            className="relative aspect-[4/3] md:aspect-auto md:min-h-[200px] rounded-2xl overflow-hidden group cursor-default shadow-lg hover:shadow-xl transition-shadow duration-500"
          >
            <img
              src={aboutHero3}
              alt="About Ayra"
              className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/10 via-transparent to-transparent opacity-60" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Team Section
const TeamSection = () => {
  const teamMembers = [
    { name: "Sameer Khan", title: "Founder & CEO", photo: sameerKhanPhoto },
    { name: "Juan Garcia", title: "COO", photo: juanGarciaPhoto },
    { name: "Adham", title: "CTO", photo: adhamPhoto },
    { name: "Michelle", title: "Content & Marketing", photo: michellePhoto },
    { name: "Salah", title: "Head of Frontend Design", photo: salahPhoto },
    { name: "Casey Williams", title: "Customer Success Manager", photo: caseyPhoto },
  ];

  return (
    <section className="py-20 md:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <AnimatedSection className="text-center mb-16 md:mb-20">
          <span className="inline-block px-4 py-1.5 mb-6 text-xs font-medium tracking-wide uppercase text-primary bg-primary/10 rounded-full">
            Our Team
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground tracking-tight mb-4">
            Meet the people behind <span className="ayra-accent">Ayra</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            A small team obsessed with building the best AI platform for agencies and businesses.
          </p>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 max-w-5xl mx-auto">
          {teamMembers.map((member, index) => (
            <AnimatedSection key={member.name} delay={index * 0.1}>
              <motion.div
                whileHover={{ y: -4, scale: 1.02 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="group cursor-default"
              >
                {/* Photo */}
                <div className="relative aspect-square mb-5 rounded-xl overflow-hidden bg-muted">
                  {member.photo ? (
                    <img
                      src={member.photo}
                      alt={member.name}
                      className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-muted to-accent/10" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-24 h-24 rounded-full bg-muted-foreground/10 flex items-center justify-center">
                          <span className="text-4xl font-semibold text-muted-foreground/30">
                            {member.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                  <motion.div
                    className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  />
                </div>

                {/* Name & Title */}
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-foreground mb-1">
                    {member.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {member.title}
                  </p>
                </div>
              </motion.div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// Partners/Logos Marquee Section
const PartnersSection = () => {
  const partners = ["Anthropic", "OpenAI", "Google Cloud", "AWS", "Stripe", "Twilio", "HubSpot", "Salesforce"];

  return (
    <section className="py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 mb-12">
        <AnimatedSection className="text-center">
          <p className="text-sm uppercase tracking-wider text-muted-foreground">
            Trusted by leading AI agencies worldwide
          </p>
        </AnimatedSection>
      </div>

      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-background to-transparent z-10" />
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-background to-transparent z-10" />

        <motion.div
          animate={{ x: [0, -1920] }}
          transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
          className="flex gap-16 items-center"
        >
          {[...partners, ...partners, ...partners, ...partners].map((partner, index) => (
            <div
              key={index}
              className="flex-shrink-0 text-xl md:text-2xl font-semibold text-muted-foreground/50 hover:text-muted-foreground transition-colors duration-300"
            >
              {partner}
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTA = () => {
  return (
    <section className="py-20 md:py-32 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
      <motion.div
        animate={{
          backgroundPosition: ["0% 0%", "100% 100%"],
        }}
        transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
        className="absolute inset-0 opacity-30"
        style={{
          background: "radial-gradient(circle at 50% 50%, hsl(var(--primary) / 0.1) 0%, transparent 50%)",
          backgroundSize: "100% 100%",
        }}
      />

      <div className="container mx-auto px-4 md:px-6 relative">
        <AnimatedSection className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground tracking-tight mb-6">
            Ready to deploy <span className="ayra-accent">AI voice agents</span>?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
            Join businesses and agencies already using Ayra to capture leads,
            book appointments, and scale with AI.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://calendly.com/sameer-ayra/30min" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-foreground text-background hover:bg-foreground/90 text-base px-8">
                Book a Call <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </a>
            <a href="/call">
              <Button size="lg" variant="outline" className="text-base px-8">
                Try the Demo
              </Button>
            </a>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Main About Page
const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <AboutHero />
        <TeamSection />
        <PartnersSection />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
};

export default About;
